/**
 * Project P&L — Studio ERP (Aug 2026). Revenue (Net Received from invoices),
 * Direct Costs (project-linked expenses incl. project-specific Petty Cash),
 * Resource Cost (logged hours × the Registry seat cost active at the time),
 * and the resulting Net Profit / Margin. Visible to Founder, Admin, and this
 * project's Client Manager.
 *
 * Oct 2026 — Period filter: the P&L can be scoped to "This month", the Indian
 * financial year (April–March, the default), "Total" (all-time), or a custom
 * date range, and shows a month-by-month breakdown for the selected window.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { fmt } from "@/lib/finance";
import { StatCard } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TrendingUp, TrendingDown, Download } from "lucide-react";

// Indian financial year runs April → March.
function currentFinancialYear() {
  const d = new Date();
  const y = d.getFullYear();
  const startYear = d.getMonth() + 1 >= 4 ? y : y - 1;
  return {
    start: `${startYear}-04-01`,
    end: `${startYear + 1}-03-31`,
    label: `FY ${startYear}–${String(startYear + 1).slice(2)}`,
  };
}
function currentMonthRange() {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const last = new Date(y, m, 0).getDate();
  const mm = String(m).padStart(2, "0");
  return { start: `${y}-${mm}-01`, end: `${y}-${mm}-${String(last).padStart(2, "0")}` };
}

export default function ProjectPnLSection({ projectId }) {
  const [pnl, setPnl] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [breakdownKind, setBreakdownKind] = useState(null); // "revenue" | "direct_costs" | null
  const [filter, setFilter] = useState("fy"); // fy | this_month | total | custom
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const fy = useMemo(currentFinancialYear, []);
  const range = useMemo(() => {
    if (filter === "fy") return { start: fy.start, end: fy.end };
    if (filter === "this_month") return currentMonthRange();
    if (filter === "custom") return { start: customStart || undefined, end: customEnd || undefined };
    return {}; // total = all-time
  }, [filter, customStart, customEnd, fy]);

  useEffect(() => {
    setPnl(null);
    setErrorMsg(null);
    const params = {};
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    api.get(`/projects/${projectId}/pnl`, { params })
      .then(({ data }) => setPnl(data))
      .catch((e) => {
        setErrorMsg(e?.response?.status === 403
          ? "You don't have access to this project's P&L."
          : "Couldn't load P&L — please try again.");
      });
  }, [projectId, range.start, range.end]);

  const positive = pnl && pnl.net_profit >= 0;
  const downloadPdf = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/pnl/export`, "_blank");

  const FILTERS = [
    { key: "this_month", label: "This month" },
    { key: "fy", label: fy.label },
    { key: "total", label: "Total" },
    { key: "custom", label: "Custom" },
  ];

  return (
    <div data-testid="project-pnl-section">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="inline-flex rounded-sm border border-slate-200 overflow-hidden" data-testid="pnl-period-filter">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-xs font-medium border-r border-slate-200 last:border-r-0 ${filter === f.key ? "bg-[#F54800] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid={`pnl-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" className="rounded-sm h-8 text-xs" onClick={downloadPdf} data-testid="pnl-download-pdf-btn">
          <Download size={12} className="mr-1.5" /> Download PDF
        </Button>
      </div>

      {filter === "custom" && (
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="pnl-custom-range">
          <Input type="date" className="rounded-sm h-8 text-xs w-auto" value={customStart} onChange={(e) => setCustomStart(e.target.value)} data-testid="pnl-custom-start" />
          <span className="text-xs text-slate-400">to</span>
          <Input type="date" className="rounded-sm h-8 text-xs w-auto" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} data-testid="pnl-custom-end" />
        </div>
      )}

      {errorMsg && <div className="text-xs text-slate-400 p-4" data-testid="pnl-error">{errorMsg}</div>}
      {!pnl && !errorMsg && <div className="text-xs text-slate-400 p-4">Loading…</div>}

      {pnl && !errorMsg && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <StatCard
              label="Revenue" value={fmt(pnl.revenue)} data-testid="pnl-revenue"
              onClick={() => setBreakdownKind("revenue")}
              hint={`${pnl.revenue_breakdown?.length || 0} invoice${pnl.revenue_breakdown?.length === 1 ? "" : "s"} · click to view`}
            />
            <StatCard
              label="Direct costs" value={fmt(pnl.direct_costs)} accent="text-orange-700" data-testid="pnl-direct-costs"
              onClick={() => setBreakdownKind("direct_costs")}
              hint={`${pnl.direct_cost_breakdown?.length || 0} expense${pnl.direct_cost_breakdown?.length === 1 ? "" : "s"} · click to view`}
            />
            <StatCard label="Resource cost" value={fmt(pnl.resource_cost)} accent="text-orange-700" data-testid="pnl-resource-cost" />
            <StatCard
              label="Net profit"
              value={<span className="inline-flex items-center gap-1">{positive ? <TrendingUp size={14} /> : <TrendingDown size={14} />} {fmt(pnl.net_profit)}</span>}
              hint={pnl.margin_pct != null ? `${pnl.margin_pct}% margin` : undefined}
              accent={positive ? "text-green-700" : "text-red-700"}
              data-testid="pnl-net-profit"
            />
          </div>

          {pnl.monthly?.length > 0 && (
            <div className="card-flat overflow-x-auto mb-4">
              <div className="px-2 pt-2 label-overline text-slate-500">Monthly breakdown</div>
              <table className="w-full text-xs" data-testid="pnl-monthly-breakdown">
                <thead className="bg-slate-50">
                  <tr>
                    {["Month", "Revenue", "Direct costs", "Resource cost", "Net profit"].map((h) => (
                      <th key={h} className="label-overline px-2 py-2 text-left whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pnl.monthly.map((m) => (
                    <tr key={m.month} data-testid={`pnl-month-${m.month}`}>
                      <td className="px-2 py-2 whitespace-nowrap">{m.month}</td>
                      <td className="px-2 py-2 font-mono text-right">{fmt(m.revenue)}</td>
                      <td className="px-2 py-2 font-mono text-right text-orange-700">{fmt(m.direct_costs)}</td>
                      <td className="px-2 py-2 font-mono text-right text-orange-700">{fmt(m.resource_cost)}</td>
                      <td className={`px-2 py-2 font-mono text-right font-semibold ${m.net_profit >= 0 ? "text-green-700" : "text-red-700"}`}>{fmt(m.net_profit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pnl.resource_breakdown?.length > 0 && (
            <div className="card-flat overflow-x-auto">
              <table className="w-full text-xs" data-testid="pnl-resource-breakdown">
                <thead className="bg-slate-50">
                  <tr>
                    {["Month", "Person", "Hours", "Hourly rate", "Cost"].map((h) => (
                      <th key={h} className="label-overline px-2 py-2 text-left whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pnl.resource_breakdown.map((r) => (
                    <tr key={`${r.user_id}-${r.month}`}>
                      <td className="px-2 py-2 whitespace-nowrap">{r.month}</td>
                      <td className="px-2 py-2">{r.user_name || "—"}</td>
                      <td className="px-2 py-2">{r.hours}h</td>
                      <td className="px-2 py-2">{r.hourly_rate != null ? fmt(r.hourly_rate) : "no seat cost set"}</td>
                      <td className="px-2 py-2 font-mono text-right">{r.cost != null ? fmt(r.cost) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <Dialog open={!!breakdownKind} onOpenChange={(o) => !o && setBreakdownKind(null)}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="pnl-breakdown-modal">
          <DialogHeader>
            <DialogTitle className="font-display">
              {breakdownKind === "revenue" ? "Revenue — invoice breakdown" : "Direct costs — expense breakdown"}
            </DialogTitle>
          </DialogHeader>
          {(() => {
            const rows = breakdownKind === "revenue" ? (pnl?.revenue_breakdown || []) : (pnl?.direct_cost_breakdown || []);
            const total = rows.reduce((s, r) => s + (r.amount || 0), 0);
            return (
              <div className="card-flat overflow-x-auto">
                <table className="w-full text-xs" data-testid="pnl-breakdown-table">
                  <thead className="bg-slate-50">
                    <tr>
                      {["Date", breakdownKind === "revenue" ? "Invoice" : "Description", breakdownKind === "revenue" ? "Client" : "Vendor", "Amount"].map((h) => (
                        <th key={h} className="label-overline px-2 py-2 text-left whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((r) => (
                      <tr key={r.id} data-testid={`pnl-breakdown-row-${r.id}`}>
                        <td className="px-2 py-2 whitespace-nowrap text-slate-500">{r.date || "—"}</td>
                        <td className="px-2 py-2">{r.label}</td>
                        <td className="px-2 py-2 text-slate-500">{r.party || "—"}</td>
                        <td className="px-2 py-2 font-mono text-right">{fmt(r.amount)}</td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr><td colSpan={4} className="px-2 py-6 text-center text-slate-400">No entries.</td></tr>
                    )}
                  </tbody>
                  {rows.length > 0 && (
                    <tfoot>
                      <tr className="border-t-2 border-slate-200 font-semibold">
                        <td colSpan={3} className="px-2 py-2 text-right">Total</td>
                        <td className="px-2 py-2 font-mono text-right">{fmt(total)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
